import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useTemplateSx from '../../hooks/useTemplateSx';
import { radiusPx } from '../../../../theme/tokens';

export default function TemplatePageHeader({
  onCreate,
  onSync,
  onOpenHealth,
  onCheckPageHealth,
  isCheckingPageHealth = false,
  pageHealthProgress = null,
  showCreate,
  showSync,
  showHealth = true,
  showCheckPageHealth = false,
  isHealthView = false,
  isSyncing,
  lastSyncedAt,
}) {
  const styles = useTemplateSx();

  const syncTooltip = lastSyncedAt
    ? `Last synced ${new Date(lastSyncedAt).toLocaleString()}`
    : 'Pull latest templates from WhatsApp provider';

  return (
    <Box sx={{
        ...styles.pageHeader,
        mt: { xs: 1.8, sm: 2.5 },
        mb: { xs: 1.8, sm: 3 },
        alignItems: 'start'
      }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{
            bgcolor: '#ebf5ef',
            width: 48,
            height: 48,
            borderRadius: radiusPx.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',              
            }}>
            <ArticleOutlinedIcon sx={{ color: 'primary.main', fontSize: 21}} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.4 }}>
            <Typography variant="h1" sx={styles.pageTitle}>
              Templates
            </Typography>
            <Typography variant="body2" sx={styles.pageSubtitle}>
              Manage WhatsApp message templates for your organisation
            </Typography>
          </Box>
      </Box>

      {showCreate || showSync || showHealth || showCheckPageHealth ? (
        <Box sx={styles.headerActions}>
          {showCheckPageHealth && onCheckPageHealth && !isHealthView ? (
            <Button
              variant="outlined"
              startIcon={
                isCheckingPageHealth ? (
                  <CircularProgress size={16} sx={{ color: '#059669' }} />
                ) : (
                  <MonitorHeartIcon sx={{ color: '#059669' }} />
                )
              }
              onClick={onCheckPageHealth}
              disabled={isCheckingPageHealth}
              sx={{
                ...styles.syncButton,
                borderRadius: radiusPx.md,
                borderColor: '#10b981',
                color: '#059669',
                '&:hover': {
                  borderColor: '#059669',
                  bgcolor: '#ecfdf5',
                },
              }}
            >
              {isCheckingPageHealth
                ? `Checking (${pageHealthProgress?.current || 0}/${pageHealthProgress?.total || 0})...`
                : 'Check Template Health'}
            </Button>
          ) : null}
          {showHealth && onOpenHealth && isHealthView ? (
            <Button
              variant={isHealthView ? "contained" : "outlined"}
              startIcon={isHealthView ? <ArrowBackIcon /> : <MonitorHeartIcon sx={{ color: isHealthView ? 'inherit' : '#059669' }} />}
              onClick={onOpenHealth}
              sx={{
                ...styles.syncButton,
                borderRadius: radiusPx.md,
                borderColor: '#10b981',
                color: isHealthView ? '#ffffff' : '#059669',
                bgcolor: isHealthView ? '#059669' : undefined,
                '&:hover': {
                  borderColor: '#059669',
                  bgcolor: isHealthView ? '#047857' : '#ecfdf5',
                },
              }}
            >
              {isHealthView ? 'Back to Templates' : 'WABA Health Studio'}
            </Button>
          ) : null}
          {showSync && !isHealthView ? (
            <Tooltip title={syncTooltip} arrow placement="top">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={onSync}
                  disabled={isSyncing}
                  sx={{ ...styles.syncButton, borderRadius: radiusPx.md }}
                >
                  {isSyncing ? 'Syncing...' : 'Sync from Provider'}
                </Button>
              </span>
            </Tooltip>
          ) : null}
          {showCreate && !isHealthView ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreate}
              sx={{ ...styles.createButton, borderRadius: radiusPx.md }}
            >
              Add Template
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
